import { AsyncPipe, UpperCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DeviceEnum } from '../../core';
import { AnatomicalSiteEnum, IPlanControl, PlanControl, planErrorMsgs, PlanForm } from '../../models';
import { getAnatomicalSide, getBoneType, PlanFormService } from '../../services';
import { AnatomicalSiteLabelPipe, BaseComponent, ValidationMessagePipe } from '../../shared';
import { BoneSelectionComponent } from '../bone-selection';


/**
 * Plan bone section component.
 */
@Component({
	selector: 'plan-bone',
	templateUrl: './plan-bone.component.html',
	imports: [
		AsyncPipe,
		UpperCasePipe,
		BoneSelectionComponent,
		AnatomicalSiteLabelPipe,
		ValidationMessagePipe
	],
	changeDetection: ChangeDetectionStrategy.Eager
})
export class PlanBoneComponent extends BaseComponent implements OnInit, OnDestroy {

	private readonly planFormSrv = inject(PlanFormService);

	isDisabled = false;
	isSkeletonLoading = false;
	availableSkeletonSites: AnatomicalSiteEnum[] | null = null;

	private form: FormGroup<IPlanControl> = new FormGroup<IPlanControl>(new PlanControl());
	errorMsgs = planErrorMsgs;
	private _resizeEvt?: NodeJS.Timeout;


	ngOnInit(): void {
		this.planFormSrv.getForm().subscribe(form => {
			this.form = form;
			this.availableSkeletonSites = this._availableSkeletonSites;
			this.isDisabled = this.planFormSrv.isReadonly || !!this.planFormSrv.plan?.isCheckUp;
		});
		window.addEventListener('resize', this.skeletonLoadingEvent);
		this.skeletonLoadingEvent();
	}

	private readonly skeletonLoadingEvent = () => {
		clearTimeout(this._resizeEvt);
		this.isSkeletonLoading = true;
		this._resizeEvt = setTimeout(() => this.isSkeletonLoading = false, 200);
	};

	private get _availableSkeletonSites(): AnatomicalSiteEnum[] | null {
		switch (this.form.value.device) {
			case DeviceEnum.Fitbone: return [
				AnatomicalSiteEnum.RightLongLeg,
				AnatomicalSiteEnum.LeftLongLeg
			];
			case DeviceEnum.Jps: return [
				AnatomicalSiteEnum.RightLongLeg,
				AnatomicalSiteEnum.RightFemur,
				AnatomicalSiteEnum.RightTibia,
				AnatomicalSiteEnum.LeftLongLeg,
				AnatomicalSiteEnum.LeftFemur,
				AnatomicalSiteEnum.LeftTibia
			];
			default: return null;
		}
	}

	get formValue(): PlanForm { return this.form?.value as PlanForm; };
	get formCtrl(): IPlanControl { return this.form?.controls; }

	/**
	* Update plan form according anatomical site selection.
	*/
	updateAnatomicalSite(site: AnatomicalSiteEnum): void {
		if (!this.isDisabled) {
			this.formCtrl.anatomicalSite.setValue(site);
			this.formCtrl.side.setValue(getAnatomicalSide(site));
			this.formCtrl.boneType.setValue(getBoneType(site));
			this.form.markAsDirty();
		}
	}


	ngOnDestroy(): void {
		window.removeEventListener('resize', this.skeletonLoadingEvent);
	}
}
